import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Paper, 
  Typography, 
  Container, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  InputAdornment,
  Button
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSearch, faCircleQuestion, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import TitleSection from '../utils/TittleTheme';
import SidebarNavigation from '../utils/SideBarNavigation';
import { dataPesquisa } from '../adm/admin/firebaseConfig';
import { getConfig } from '../adm/ConfigContext';

const Contato = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState(false);
  const config = getConfig();

  // Manipulador para expandir apenas uma sanfona por vez (estilo exclusivo)
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Filtro dinâmico baseado no input do usuário e nas keywords do arquivo de dados
  const filteredFAQ = dataPesquisa.filter((faq) => {
    const query = searchTerm.toLowerCase().trim();
    if (!query) return true;

    return (
      faq.question.toLowerCase().includes(query) ||
      faq.keywords.some((keyword) => keyword.toLowerCase().includes(query))
    );
  });

  return (
    <Container maxWidth="lg" sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, py: 5 }}>
      <Box sx={{ flex: 4, maxWidth: { xs: 600, md: 800 }, mx: 'auto', width: '100%' }}>
        
        <TitleSection 
          title={'Central de Ajuda e FAQ'} 
          novo={true} 
          subtitle={'Encontre respostas rápidas para as principais dúvidas sobre os serviços, legislação e transparência da Câmara e Prefeitura. Digite o que procura ou navegue pelos tópicos abaixo.'}
        />

        {/* Barra de Pesquisa Moderna */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: '4px 12px', 
            display: 'flex', 
            alignItems: 'center', 
            borderRadius: '30px',
            border: '1px solid #e0e0e0',
            backgroundColor: '#fcfcfc',
            mb: 4,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.03)',
            transition: 'all 0.3s ease',
            '&:focus-within': {
              borderColor: config?.color || '#004d99',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'
            }
          }}
        >
          <TextField
            fullWidth
            variant="standard"
            placeholder="Digite uma palavra-chave (ex: Licitações, Impostos, Vereadores...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 1, mr: 1, color: '#888' }}>
                  <FontAwesomeIcon icon={faSearch} />
                </InputAdornment>
              ),
            }}
            sx={{ py: 1 }}
          />
        </Paper>

        {/* Lista de Questionamentos (FAQ) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredFAQ.length > 0 ? (
            filteredFAQ.map((faq, index) => {
              const panelId = `panel-${index}`;
              const isCurrentExpanded = expanded === panelId;

              return (
                <Accordion
                  key={index}
                  expanded={isCurrentExpanded}
                  onChange={handleChange(panelId)}
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: '1px solid #eef2f6',
                    backgroundColor: isCurrentExpanded ? '#fafafa' : '#ffffff',
                    boxShadow: isCurrentExpanded ? '0px 4px 20px rgba(0,0,0,0.04)' : '0px 2px 4px rgba(0,0,0,0.01)',
                    transition: 'all 0.2s ease-in-out',
                    '&:before': { display: 'none' }, // Remove a linha nativa do MUI Accordion
                    '&:hover': {
                      borderColor: '#d0d7de',
                      backgroundColor: '#f9fbfe'
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <FontAwesomeIcon 
                        icon={faChevronDown} 
                        style={{ 
                          fontSize: '14px', 
                          color: isCurrentExpanded ? (config?.color || '#004d99') : '#666',
                          transition: 'transform 0.3s'
                        }} 
                      />
                    }
                    sx={{
                      px: 3,
                      py: 1,
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        gap: 2
                      }
                    }}
                  >
                    <FontAwesomeIcon 
                      icon={faCircleQuestion} 
                      style={{ 
                        color: isCurrentExpanded ? (config?.color || '#004d99') : '#94a3b8',
                        fontSize: '18px'
                      }} 
                    />
                    <Typography 
                      sx={{ 
                        fontWeight: 600, 
                        color: isCurrentExpanded ? '#1e293b' : '#334155',
                        fontSize: { xs: '0.95rem', md: '1.05rem' }
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 3, pb: 3, pt: 1 }}>
                    <Typography 
                      sx={{ 
                        color: '#475569', 
                        lineHeight: 1.6, 
                        fontSize: '0.95rem',
                        mb: 2 
                      }}
                    >
                      {faq.answer}
                    </Typography>

                    {/* Botão de Ação Direcionando para o Link */}
                    {faq.link && (
                      <Button
                        component={Link}
                        to={faq.link}
                        variant="outlined"
                        size="small"
                        endIcon={<FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '12px' }} />}
                        sx={{
                          borderColor: config?.color || '#004d99',
                          color: config?.color || '#004d99',
                          textTransform: 'none',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          px: 2,
                          '&:hover': {
                            backgroundColor: `${config?.color}10` || '#004d9910',
                            borderColor: config?.color || '#004d99',
                          }
                        }}
                      >
                        Acessar Página
                      </Button>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })
          ) : (
            /* Estado de Nenhum Resultado Encontrado */
            <Paper 
              elevation={0} 
              sx={{ 
                p: 5, 
                textAlign: 'center', 
                border: '1px dashed #cbd5e1', 
                borderRadius: '12px',
                backgroundColor: '#f8fafc' 
              }}
            >
              <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 600, mb: 1 }}>
                Nenhum resultado encontrado
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Não encontramos tópicos correspondentes a "{searchTerm}". Tente usar palavras mais simples como "licitação", "diário" ou "IPTU".
              </Typography>
            </Paper>
          )}
        </Box>

      </Box>

      {/* Menu Lateral de Navegação */}
      <Box sx={{ flex: { xs: 1, md: 1.5 }, minWidth: 250 }} marginTop={5}>
        <SidebarNavigation noticia={true} />
      </Box>
    </Container>
  );
};

export default Contato;